import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from "@mui/icons-material/Logout";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { getAllUsersFunc } from '../../lib/helper';
import EmailIcon from '@mui/icons-material/MailOutlineRounded';

type NavItem = {
    id: string;
    icon: React.ElementType;
    label: string;
    route: string[];
};

const useStyles = makeStyles(() => ({
    sidebarContainer: {
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        zIndex: 1200,
    },
    sidebarCollapsed: {
        width: '85px',
        height: '100vh',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e5e7eb',
    },
    sidebarExpanded: {
        width: '256px',
        height: '100vh',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e5e7eb',
        boxShadow: '4px 0 12px rgba(0, 0, 0, 0.1)',
    },
    logoSection: {
        height: '64px',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 16px',
    },
    logo: {
        width: '32px',
        height: '32px',
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        borderRadius: '8px',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '18px',
    },
    logoText: {
        fontWeight: 600,
        color: '#111827',
    },
    toggleButton: {
        padding: '6px',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    navSection: {
        flex: 1,
        padding: '16px 12px',
    },
    navButton: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        transition: 'all 0.2s',
        textTransform: 'none',
        justifyContent: 'flex-start',
        minWidth: 0,
    },
    navButtonActive: {
        backgroundColor: '#f0fdfa',
        color: '#0f766e',
        '&:hover': {
            backgroundColor: '#f0fdfa',
        },
    },
    navButtonInactive: {
        color: '#374151',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    navIcon: {
        width: '20px',
        height: '20px',
        flexShrink: 0,
    },
    navIconActive: {
        color: '#0d9488',
    },
    navIconInactive: {
        color: '#6b7280',
    },
    navLabel: {
        flex: 1,
        textAlign: 'left',
        fontWeight: 500,
    },
    userSection: {
        borderTop: '1px solid #e5e7eb',
        padding: '16px',
    },
    avatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '14px',
    },
    userName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
    },
    userEmail: {
        fontSize: '12px',
        color: '#6b7280',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));

const HomeIcon: React.FC<{ color?: string }> = ({ color = "currentColor" }) => (
    <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
);

const SettingsIcon: React.FC<{ color?: string }> = ({ color = "currentColor" }) => (
    <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
        <circle cx="12" cy="12" r="3" />
    </svg>
);


export const IconAtom: React.FC<{
    icon: React.ElementType;
    className: string;
}> = ({ icon: Icon, className }) => {
    return <Icon className={className} />;
};

export const TextAtom: React.FC<{
    text: string;
    className: string;
}> = ({ text, className }) => {
    return <Typography className={className}>{text}</Typography>;
};

export const LogoBoxAtom: React.FC<{
    letter: string;
}> = ({ letter }) => {
    const classes = useStyles();
    return (
        <Stack className={classes.logo} direction="row" alignItems="center" justifyContent="center">
            <Typography>{letter}</Typography>
        </Stack>
    );
};

export const AvatarAtom: React.FC<{
    initials: string;
}> = ({ initials }) => {
    const classes = useStyles();
    return (
        <Stack className={classes.avatar} direction="row" alignItems="center" justifyContent="center">
            <Typography>{initials}</Typography>
        </Stack>
    );
};

export const ToggleButtonAtom: React.FC<{
    isExpanded: boolean;
    onClick: () => void;
}> = ({ isExpanded, onClick }) => {
    const classes = useStyles();
    return (
        <IconButton onClick={onClick} className={classes.toggleButton}>
            {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
    );
};

export const LogoMolecule: React.FC<{
    isExpanded: boolean;
}> = ({ isExpanded }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);

    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <LogoBoxAtom letter={userGroup.charAt(0)} />
            {isExpanded && <TextAtom text={userGroup} className={classes.logoText} />}
        </Stack>
    );
};

export const NavItemMolecule: React.FC<{
    icon: React.ElementType;
    label: string;
    isActive: boolean;
    isExpanded: boolean;
    onClick: () => void;
}> = ({
    icon,
    label,
    isActive,
    isExpanded,
    onClick,
}) => {
    const classes = useStyles();
    const buttonClassName = `${classes.navButton} ${isActive ? classes.navButtonActive : classes.navButtonInactive}`;
    const iconClassName = `${classes.navIcon} ${isActive ? classes.navIconActive : classes.navIconInactive}`;

    return (<>{!isExpanded ? (
        <Tooltip title={label} placement="right"
                componentsProps={{ tooltip: { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'auto',
                    height: 'fit-content',
                    fontSize: '1rem',
                }}}}>
            <Button onClick={onClick} className={buttonClassName} sx={{ width: 'fit-content !important' }}>
                <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                    <IconAtom icon={icon} className={iconClassName} />
                </Stack>
            </Button>
        </Tooltip>
    ) : (
        <Button onClick={onClick} className={buttonClassName} sx={{ width: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                <IconAtom icon={icon} className={iconClassName} />
                <TextAtom text={label} className={classes.navLabel} />
            </Stack>
        </Button>
    )}</>);
};

export const UserProfileMolecule: React.FC<{
    isExpanded: boolean;
    name: string;
    email: string;
    initials: string;
}> = ({
    isExpanded,
    name,
    email,
    initials,
}) => {
    const classes = useStyles();
    const { logout } = useContext(AuthContext);

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-start">
                <AvatarAtom initials={initials} />
                {isExpanded && (
                    <Stack direction="column" spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                        <TextAtom text={name} className={classes.userName} />
                        <TextAtom text={email} className={classes.userEmail} />
                    </Stack>
                )}
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="flex-end">
                {isExpanded && (
                    <Tooltip title={'Sign Out'} placement="right"
                        componentsProps={{ tooltip: { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 'auto',
                            height: 'fit-content',
                            fontSize: '1rem',
                        }}}}>
                        <IconButton onClick={logout} >
                            <LogoutIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Stack>
    );
};

export const NavigationOrganism: React.FC<{
    items: NavItem[];
    activeId: string;
    isExpanded: boolean;
    onItemClick: (id: string) => void;
}> = ({
    items,
    activeId,
    isExpanded,
    onItemClick,
}) => {
    return (
        <Stack direction="column" spacing={0.5} alignItems="center">
            {items.map((item) => {
                const isActive = item.id === activeId;

                return (
                    <NavItemMolecule
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        isActive={isActive}
                        isExpanded={isExpanded}
                        onClick={() => onItemClick(item.id)}
                    />
                );
            })}
        </Stack>
    );
};

export const SidebarTemplate: React.FC<{
    isExpanded: boolean;
    onToggle: () => void;
    activeNavId: string;
    onNavChange: (id: string) => void;
    navItems: NavItem[];
    userName: string;
    userEmail: string;
    userInitials: string;
}> = ({
    isExpanded,
    onToggle,
    activeNavId,
    onNavChange,
    navItems,
    userName,
    userEmail,
    userInitials,
}) => {
    const classes = useStyles();

    return (
        <Box className={classes.sidebarContainer}>
            <Stack
                direction="column"
                className={isExpanded ? classes.sidebarExpanded : classes.sidebarCollapsed}
                sx={{ transition: 'width 0.3s ease-in-out' }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    className={classes.logoSection}
                >
                    <LogoMolecule isExpanded={isExpanded} />
                    <ToggleButtonAtom isExpanded={isExpanded} onClick={onToggle} />
                </Stack>

                <Box className={classes.navSection}>
                    <NavigationOrganism
                        items={navItems}
                        activeId={activeNavId}
                        isExpanded={isExpanded}
                        onItemClick={onNavChange}
                    />
                </Box>

                <Box className={classes.userSection}>
                    <UserProfileMolecule
                        isExpanded={isExpanded}
                        name={userName}
                        email={userEmail}
                        initials={userInitials}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export const Sidebar: React.FC<{}> = () => {
    const history = useHistory();
    const { userGroup, user } = useContext(AuthContext);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [activeNavId, setActiveNavId] = useState<string>('home');
    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<{firstName: string, lastName: string}>({firstName: '', lastName: ''});

    const initials = useMemo(() =>
        name ? `${name.firstName.charAt(0).toUpperCase()}${name.lastName.charAt(0).toUpperCase()}` : ''
    , [name]);

    const userName = useMemo(() =>
        name ? `${name.firstName} ${name.lastName}` : '<User>'
    , [name]);

    const navItems = useMemo<NavItem[]>(() => [
        { id: 'home', icon: HomeIcon, label: 'Home', route: ['/home'] },
        { id: 'audit', icon: EmailIcon, label: 'Email Index', route: ['/audit'] },
        { id: 'settings', icon: SettingsIcon, label: 'Settings', route: ['/settings'] },
    ], []);

    const handleToggle = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const handleNavChange = useCallback((id: string) => {
        setActiveNavId(id);
        history.push(navItems.find(item => item.id === id)?.route[0] || '/home');
    }, [history, navItems]);

    useEffect(() => {
        const syncActiveFromPath = (pathname: string) => {
            const matched = navItems.find(item => item.route.some(r => pathname.startsWith(r)));
            if (matched) setActiveNavId(matched.id);
        };
        syncActiveFromPath(history.location.pathname);
        return history.listen(({ pathname }) => syncActiveFromPath(pathname));
    }, [history, navItems]);

    useEffect(() => {
        getAllUsersFunc(userGroup).then((users) => {
            const lookUpUser = users.find(({ username }) => username === user.username);

            setEmail(user?.attributes?.email?.toLowerCase() || lookUpUser?.email?.toLowerCase());
            setName({ firstName: lookUpUser?.firstName?.toLowerCase() || '', lastName: lookUpUser?.lastName?.toLowerCase() || '' });
        });
    }, [userGroup, user]);

    return (
        <SidebarTemplate
            isExpanded={isExpanded}
            onToggle={handleToggle}
            activeNavId={activeNavId}
            onNavChange={handleNavChange}
            navItems={navItems}
            userName={userName}
            userEmail={email}
            userInitials={initials}
        />
    );
};

export default Sidebar;